import { FormsService, PersonFormRow } from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { Dialog } from "components/Dialog";
import { errorText, sendChosenForm } from "components/Forms/formsApi";
import ResponseDialog, { formatDate } from "components/Forms/ResponseDialog";
import {
  ChannelChecks,
  FormPicker,
  audienceFor,
  noFormsText,
  useForms,
} from "components/Forms/SendFormControls";
import ViewButton from "components/Forms/ViewButton";
import { useEffect, useState } from "react";
import ResendFormDialog, { FormGroup } from "./ResendFormDialog";
import {
  OutstandingForm,
  ROLE_OF,
  StepDialogProps,
  StepFrame,
  linkExpired,
  nameAndRole,
} from "./shared";

/** The forms not filled out yet, one entry per form, in the order sent. */
function byForm(forms: OutstandingForm[]): FormGroup[] {
  const groups: FormGroup[] = [];
  for (const form of forms) {
    const group = groups.find((g) => g.formId === form.form_id);
    if (group) group.sends.push(form);
    else
      groups.push({
        formId: form.form_id,
        formName: form.form_name,
        sends: [form],
      });
  }
  return groups;
}

/** Who a form is waiting on: the one person, or any of those it went to. */
function waitingOn(sends: OutstandingForm[]): string {
  const names = sends.map((s) => nameAndRole(s.sent_to));
  if (names.length === 1) return `Waiting on ${names[0]}`;
  const last = names.pop();
  return `${names.length === 1 ? "Either" : "Any of"} ${names.join(
    ", "
  )} or ${last} can fill it out`;
}

function firstName(send: OutstandingForm): string {
  return send.sent_to.first_name || nameAndRole(send.sent_to);
}

/** When a form was sent: one date, or each person's when they differ. */
function sentText(
  sends: OutstandingForm[],
  sentAt: (s: OutstandingForm) => string
) {
  const dates = sends.map((s) => formatDate(sentAt(s)));
  if (dates.every((d) => d === dates[0])) {
    if (sends.length === 1) return `Sent ${dates[0]}`;
    return `Sent ${dates[0]} to ${
      sends.length === 2 ? "both" : `all ${sends.length}`
    }`;
  }
  return `Sent ${sends
    .map((s, i) => `${dates[i]} to ${firstName(s)}`)
    .join(", ")}`;
}

/** Which of a form's links no longer work, if any. */
function expiredText(sends: OutstandingForm[], lapsed: OutstandingForm[]) {
  if (lapsed.length === 0) return "";
  if (lapsed.length === sends.length)
    return sends.length === 1 ? "Link expired" : "Links expired";
  if (lapsed.length === 1) return `${firstName(lapsed[0])}'s link expired`;
  return `${lapsed.length} links expired`;
}

/**
 * The Form step. Outstanding: each form sent and not filled out yet, who
 * it is waiting on (anyone it went to can fill it out), when it was sent,
 * and Resend, which opens a pop-up to send the same link again (the open
 * link for that form and person; never a second one). All filled out: the
 * responses, newest first. Nothing sent: pick a form and send it.
 */
export default function FormStepDialog({
  orgId,
  person,
  done,
  outstandingForms = [],
  onDone,
  onClose,
}: StepDialogProps & { outstandingForms?: OutstandingForm[] }) {
  const role = ROLE_OF[person.kind];
  const outstanding = outstandingForms.length > 0;
  const [responses, setResponses] = useState<PersonFormRow[]>();
  const [loadError, setLoadError] = useState<string>();
  const [shown, setShown] = useState<number | null>(null);
  const [channels, setChannels] = useState({ sms: true, email: false });
  const [sending, setSending] = useState(false);
  // Links resent in this window, and when: shown until the row reloads.
  const [resentAt, setResentAt] = useState<Record<number, string>>({});
  const [resending, setResending] = useState<FormGroup | null>(null);
  const [formId, setFormId] = useState<number | null>(null);
  const forms = useForms(
    !done && !outstanding ? orgId : null,
    audienceFor(role)
  );

  useEffect(() => {
    if (!done) return;
    FormsService.getPersonForms({
      orgId,
      personType: role,
      personId: person.id,
    })
      .then((rows) => setResponses(rows.filter((row) => row.response_id)))
      .catch((e) =>
        setLoadError(errorText(e, "The responses could not be loaded."))
      );
  }, [done, orgId, role, person.id]);

  async function send(id: number) {
    setSending(true);
    const sent = await sendChosenForm({
      orgId,
      formId: id,
      ...channels,
      personType: role,
      personId: person.id,
    });
    setSending(false);
    if (sent) onDone();
  }

  // Everything filled out, and only one response: show it as it is.
  if (done && responses?.length === 1) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <ResponseDialog responseId={responses[0].response_id as number} />
      </Dialog>
    );
  }

  if (done) {
    return (
      <StepFrame title="Forms filled out" person={person} onClose={onClose}>
        {loadError && <p className="text-sm text-red-600">{loadError}</p>}
        {!responses && !loadError && (
          <p className="text-sm text-gray-500">Loading...</p>
        )}
        {responses?.length === 0 && (
          <p className="text-sm text-gray-600">No responses to show.</p>
        )}
        <ul className="divide-y divide-gray-100">
          {responses?.map((row) => (
            <li
              key={row.response_id}
              className="flex items-center justify-between gap-3 py-2.5"
            >
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  {row.form_name}
                </span>
                <span className="block text-xs text-gray-500">
                  Filled out {formatDate(row.date)}
                  {row.filled_by ? ` by ${row.filled_by}` : ""}
                </span>
              </span>
              <ViewButton
                label={`View ${row.form_name}`}
                onClick={() => setShown(row.response_id as number)}
              />
            </li>
          ))}
        </ul>
        <Dialog
          open={shown !== null}
          onOpenChange={(open) => !open && setShown(null)}
        >
          {shown !== null && <ResponseDialog responseId={shown} />}
        </Dialog>
      </StepFrame>
    );
  }

  if (outstanding) {
    const sentAt = (s: OutstandingForm) => resentAt[s.send_id] ?? s.sent_at;
    const expired = (s: OutstandingForm) =>
      !resentAt[s.send_id] && linkExpired(s);
    return (
      <StepFrame
        title="Forms not filled out yet"
        person={person}
        onClose={onClose}
      >
        <ul className="divide-y divide-gray-100">
          {byForm(outstandingForms).map((group) => {
            const { sends } = group;
            const lapsed = expiredText(sends, sends.filter(expired));
            const allResent = sends.every((s) => resentAt[s.send_id]);
            return (
              <li
                key={group.formId}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    {group.formName}
                  </span>
                  <span className="block text-xs text-gray-700">
                    {waitingOn(sends)}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {sentText(sends, sentAt)}
                    {lapsed && (
                      <span className="text-red-500"> · {lapsed}</span>
                    )}
                  </span>
                </span>
                <Button
                  color={allResent ? ButtonColor.GRAY : ButtonColor.GREEN}
                  size={ButtonSize.SMALL}
                  aria-label={`Resend ${group.formName}`}
                  disabled={allResent}
                  onClick={() => setResending(group)}
                >
                  {allResent ? "Sent" : "Resend"}
                </Button>
              </li>
            );
          })}
        </ul>
        {resending && (
          <ResendFormDialog
            orgId={orgId}
            group={resending}
            onSent={(ids) => {
              const now = new Date().toISOString();
              setResentAt((prev) => ({
                ...prev,
                ...Object.fromEntries(ids.map((id) => [id, now])),
              }));
              onDone();
            }}
            onClose={() => setResending(null)}
          />
        )}
      </StepFrame>
    );
  }

  return (
    <StepFrame title="Send a form" person={person} onClose={onClose}>
      <p className="mb-3 text-sm text-gray-600">No form has been sent yet.</p>
      <FormPicker
        forms={forms}
        formId={formId}
        onChange={setFormId}
        placeholder="No form selected"
        disabled={sending}
        emptyText={noFormsText(audienceFor(role))}
      />
      {formId !== null && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <ChannelChecks
            sms={channels.sms}
            email={channels.email}
            onChange={setChannels}
            disabled={sending}
          />
          <Button
            color={ButtonColor.GREEN}
            size={ButtonSize.SMALL}
            disabled={sending}
            onClick={() => send(formId)}
          >
            Send
          </Button>
        </div>
      )}
    </StepFrame>
  );
}
